import { ArrowDown, ArrowUp } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
} from 'recharts';

import { cn } from '@apps/web/lib/utils';
import { type ToolStatRow } from '@libraries/typescript/api_client';

// A tool row's error rate is only alarming past this fraction of its calls.
const ERROR_RATE_ALERT = 0.05;

type SortKey = 'calls' | 'error_rate' | 'p95_duration' | 'p95_tokens' | 'args';

type SortState = {
    readonly key: SortKey;
    readonly direction: 'asc' | 'desc';
};

type ToolStatsTableProps = {
    readonly tools: Array<ToolStatRow>;
    readonly onSelectTool: (toolName: string) => void;
};

function errorRate(row: ToolStatRow): number | null {
    return row.call_count === 0 ? null : row.failed_count / row.call_count;
}

// Sort keys map to a comparable number; null (no measurement) always sinks to
// the bottom regardless of direction so empty rows never crowd the top.
const SORT_VALUE: Record<SortKey, (row: ToolStatRow) => number | null> = {
    calls: (row) => row.call_count,
    error_rate: errorRate,
    p95_duration: (row) => row.p95_duration_ms,
    p95_tokens: (row) => row.p95_turn_output_tokens,
    args: (row) => row.avg_args_chars,
};

function formatDurationMs(durationMs: number | null): string {
    if (durationMs === null) {
        return '—';
    }
    if (durationMs < 1000) {
        return `${Math.round(durationMs)}ms`;
    }
    const seconds = durationMs / 1000;
    if (seconds < 60) {
        return `${seconds.toFixed(1)}s`;
    }
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${Math.round(seconds % 60)}s`;
}

// Compact char counts: 340 stays "340", 12,400 becomes "12.4k".
function formatCompact(value: number | null): string {
    if (value === null) {
        return '—';
    }
    if (value < 1000) {
        return String(Math.round(value));
    }
    return `${(value / 1000).toFixed(1)}k`;
}

function formatTokens(value: number | null): string {
    return value === null ? '—' : formatCompact(Math.round(value));
}

function formatRate(rate: number | null): string {
    return rate === null ? '—' : `${(rate * 100).toFixed(1)}%`;
}

function CallsSparkline({ row }: { readonly row: ToolStatRow }) {
    const data = row.hourly.map((bucket) => ({
        bucketStart: bucket.bucket_start,
        callCount: bucket.call_count,
    }));
    if (data.length === 0) {
        return <span className="text-muted-foreground text-xs">—</span>;
    }
    return (
        <div className="h-8 w-28">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 2, bottom: 2 }}>
                    <RechartsTooltip
                        cursor={false}
                        content={<SparklineTooltip />}
                    />
                    <Line
                        type="monotone"
                        dataKey="callCount"
                        stroke="var(--chart-1)"
                        dot={false}
                        strokeWidth={1.5}
                        isAnimationActive={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

function SparklineTooltip({
    active,
    payload,
}: {
    readonly active?: boolean;
    readonly payload?: Array<{ readonly value?: number }>;
}) {
    if (!active || !payload?.[0]) {
        return null;
    }
    return (
        <div className="bg-background rounded-md border px-2 py-1 text-xs tabular-nums shadow-sm">
            {payload[0].value} calls/hr
        </div>
    );
}

function SortableHeader({
    label,
    sortKey,
    sort,
    onSort,
    align = 'right',
}: {
    readonly label: string;
    readonly sortKey: SortKey;
    readonly sort: SortState;
    readonly onSort: (key: SortKey) => void;
    readonly align?: 'left' | 'right';
}) {
    const active = sort.key === sortKey;
    return (
        <th
            className={cn(
                'px-3 py-2 font-medium',
                align === 'right' ? 'text-right' : 'text-left',
            )}
        >
            <button
                type="button"
                onClick={() => onSort(sortKey)}
                className={cn(
                    'hover:text-foreground inline-flex items-center gap-1',
                    align === 'right' && 'flex-row-reverse',
                    active && 'text-foreground',
                )}
            >
                <span>{label}</span>
                {active ? (
                    sort.direction === 'desc' ? (
                        <ArrowDown className="h-3 w-3" />
                    ) : (
                        <ArrowUp className="h-3 w-3" />
                    )
                ) : null}
            </button>
        </th>
    );
}

export function ToolStatsTable({ tools, onSelectTool }: ToolStatsTableProps) {
    const [sort, setSort] = useState<SortState>({
        key: 'calls',
        direction: 'desc',
    });
    const handleSort = (key: SortKey) => {
        setSort((prev) =>
            prev.key === key
                ? {
                      key,
                      direction: prev.direction === 'desc' ? 'asc' : 'desc',
                  }
                : { key, direction: 'desc' },
        );
    };
    const sortedTools = useMemo(() => {
        const accessor = SORT_VALUE[sort.key];
        const factor = sort.direction === 'desc' ? -1 : 1;
        return [...tools].sort((a, b) => {
            const aValue = accessor(a);
            const bValue = accessor(b);
            if (aValue === null && bValue === null) {
                return 0;
            }
            if (aValue === null) {
                return 1;
            }
            if (bValue === null) {
                return -1;
            }
            return (aValue - bValue) * factor;
        });
    }, [tools, sort]);
    if (tools.length === 0) {
        return (
            <p className="text-muted-foreground py-6 text-center text-sm">
                No tool calls in this window.
            </p>
        );
    }
    return (
        <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-xs whitespace-nowrap">
                <thead className="bg-muted/50 sticky top-0">
                    <tr className="text-muted-foreground text-left">
                        <th className="px-3 py-2 font-medium">Tool</th>
                        <SortableHeader
                            label="Calls"
                            sortKey="calls"
                            sort={sort}
                            onSort={handleSort}
                        />
                        <SortableHeader
                            label="Error rate"
                            sortKey="error_rate"
                            sort={sort}
                            onSort={handleSort}
                        />
                        <th className="px-3 py-2 text-right font-medium">
                            Retries
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                            p50
                        </th>
                        <SortableHeader
                            label="p95"
                            sortKey="p95_duration"
                            sort={sort}
                            onSort={handleSort}
                        />
                        <th className="px-3 py-2 text-right font-medium">
                            p95 wait
                        </th>
                        <SortableHeader
                            label="Args (avg/max)"
                            sortKey="args"
                            sort={sort}
                            onSort={handleSort}
                        />
                        <SortableHeader
                            label="Tokens (avg/p95)"
                            sortKey="p95_tokens"
                            sort={sort}
                            onSort={handleSort}
                        />
                        <th className="px-3 py-2 font-medium">48h calls</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedTools.map((row) => {
                        const rate = errorRate(row);
                        return (
                            <tr
                                key={row.tool_name}
                                onClick={() => onSelectTool(row.tool_name)}
                                className="hover:bg-muted/40 cursor-pointer border-t"
                            >
                                <td className="px-3 py-2 font-medium">
                                    {row.tool_name}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">
                                    {row.call_count}
                                </td>
                                <td
                                    className={cn(
                                        'px-3 py-2 text-right tabular-nums',
                                        rate !== null &&
                                            rate > ERROR_RATE_ALERT &&
                                            'text-red-600 dark:text-red-400',
                                    )}
                                >
                                    {formatRate(rate)}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">
                                    {row.retry_count}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">
                                    {formatDurationMs(row.p50_duration_ms)}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">
                                    {formatDurationMs(row.p95_duration_ms)}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">
                                    {formatDurationMs(row.p95_queue_wait_ms)}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">
                                    {formatCompact(row.avg_args_chars)}
                                    <span className="text-muted-foreground">
                                        {' / '}
                                        {formatCompact(row.max_args_chars)}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">
                                    {formatTokens(row.avg_turn_output_tokens)}
                                    <span className="text-muted-foreground">
                                        {' / '}
                                        {formatTokens(
                                            row.p95_turn_output_tokens,
                                        )}
                                    </span>
                                </td>
                                <td className="px-3 py-2">
                                    <CallsSparkline row={row} />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// Shared with the tool detail drawer so both surfaces format identically.
export { formatCompact, formatDurationMs, formatRate, formatTokens };
